import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { gameConfig } from '../config/gameConfig.js';
import modelLoader from './ModelLoader.js';

export class Dice {
    constructor(value = 1, isBomb = false) {
        this._value = isBomb ? -1 : value;
        this.faces = this.initializeFaces(this._value);
        this.isSelected = false;
        this.isAnimating = false;
        this.originalScale = new THREE.Vector3(1, 1, 1);
        this.originalPosition = new THREE.Vector3();
        this.selectionAnimation = null;
        this.isHighlighted = false;
        this.isPreviewing = false;
        this.isBomb = isBomb;
        this.isExploding = false;
        this.explosionStartTime = null;
        
        // 优化：缓存常用的四元数和向量
        this._quaternion = new THREE.Quaternion();
        this._vector = new THREE.Vector3();
        this._worldAxis = new THREE.Vector3();
        this._targetQuaternion = new THREE.Quaternion();

        // 触摸控制相关的属性
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchEndX = 0;
        this.touchEndY = 0;
        this.minSwipeDistance = 30;  // 最小滑动距离，小于这个距离不触发移动
        this.isTouchActive = false;
        
        // 使用预加载的骰子模型
        this.createDiceMesh().then(mesh => {
            this.mesh = mesh;
            // 根据selectedConfig中的rotation设置初始旋转角度
            if (this.selectedConfig && this.selectedConfig.rotation) {
                const { x, y, z } = this.selectedConfig.rotation;
                this.mesh.rotation.set(x, y, z);
                console.log('根据配置设置骰子初始旋转角度:', {
                    x: (x * 180 / Math.PI).toFixed(2) + '°',
                    y: (y * 180 / Math.PI).toFixed(2) + '°',
                    z: (z * 180 / Math.PI).toFixed(2) + '°'
                });
            } else {
                this.mesh.rotation.set(0, 0, 0);
                console.log('未找到旋转配置，使用默认旋转角度(0, 0, 0)');
            }
            
            this.mesh.updateMatrix();
            this.mesh.updateMatrixWorld();
            
            console.log('创建骰子，初始值:', value);
            console.log('初始面值布局:', {
                右: this.faces[0],
                左: this.faces[1],
                上: this.faces[2],
                下: this.faces[3],
                前: this.faces[4],
                后: this.faces[5]
            });
        });
    }

    // 获取模型路径的方法，可以被子类重写
    getModelPath() {
        return this.isBomb ? gameConfig.dice.models.bomb : gameConfig.dice.models.dice;
    }

    // 处理加载后的模型的方法，可以被子类重写
    processLoadedModel(model) {
        if (this.isBomb) {
            // 炸弹模型绕Z轴逆时针旋转90度
            model.rotation.z = Math.PI / 2;
        } else {
            model.scale.set(1, 1, 1);
        }
        model.position.set(0, 0, 0);
    }

    initializeFaces(value) {
        // 根据标准骰子布局初始化六个面的值
        // 标准骰子规则：对面之和为7
        // faces数组顺序：[右, 左, 上, 下, 前, 后]
        
        // 定义每个数字朝上时可能的面值分布情况
        // 每个子数组代表：[右, 左, 上, 下, 前, 后]
        this.facesConfigurations = {
            1: [
                {
                    faces: [3, 4, 1, 6, 2, 5],
                    rotation: { x: Math.PI/2, y: 0, z: 0 }
                },
                {
                    faces: [2, 5, 1, 6, 4, 3],
                    rotation: { x: Math.PI/2, y: 0, z: -Math.PI/2 }
                },
                {
                    faces: [4, 3, 1, 6, 5, 2],
                    rotation: { x: Math.PI/2, y: 0, z: Math.PI }
                },
                {
                    faces: [5, 2, 1, 6, 3, 4],
                    rotation: { x: Math.PI/2, y: 0, z: Math.PI/2 }
                }
            ],
            2: [
                {
                    faces: [4, 3, 2, 5, 1, 6],
                    rotation: { x: 0, y: Math.PI, z: 0 }
                },
                {
                    faces: [6, 1, 2, 5, 4, 3],
                    rotation: { x: 0, y: Math.PI/2, z: 0 }
                },
                {
                    faces: [3, 4, 2, 5, 6, 1],
                    rotation: { x: 0, y: 0, z: 0 }
                },
                {
                    faces: [1, 6, 2, 5, 3, 4],
                    rotation: { x: 0, y: -Math.PI/2, z: 0 }
                }
            ],
            3: [
                {
                    faces: [6, 1, 3, 4, 2, 5],
                    rotation: { x: Math.PI/2, y: Math.PI/2, z: 0 }
                },
                {
                    faces: [5, 2, 3, 4, 6, 1],
                    rotation: { x: 0, y: 0, z: Math.PI/2 }
                },
                {
                    faces: [2, 5, 3, 4, 1, 6],
                    rotation: { x: Math.PI, y: 0, z: -Math.PI/2 }
                },
                {
                    faces: [1, 6, 3, 4, 5, 2],
                    rotation: { x: -Math.PI/2, y: -Math.PI/2, z: 0 }
                }
            ],
            4: [
                {
                    faces: [1, 6, 4, 3, 2, 5],
                    rotation: { x: Math.PI/2, y: -Math.PI/2, z: 0 }
                },
                {
                    faces: [2, 5, 4, 3, 6, 1],
                    rotation: { x: 0, y: 0, z: -Math.PI/2 }
                },
                {
                    faces: [5, 2, 4, 3, 1, 6],
                    rotation: { x: Math.PI, y: 0, z: Math.PI/2 }
                },
                {
                    faces: [6, 1, 4, 3, 5, 2],
                    rotation: { x: -Math.PI/2, y: Math.PI/2, z: 0 }
                }
            ],
            5: [
                {
                    faces: [3, 4, 5, 2, 1, 6],
                    rotation: { x: Math.PI, y: 0, z: 0 }
                },
                {
                    faces: [1, 6, 5, 2, 4, 3],
                    rotation: { x: Math.PI, y: -Math.PI/2, z: 0 }
                },
                {
                    faces: [4, 3, 5, 2, 6, 1],
                    rotation: { x: 0, y: 0, z: Math.PI }
                },
                {
                    faces: [6, 1, 5, 2, 3, 4],
                    rotation: { x: Math.PI, y: Math.PI/2, z: 0 }
                }
            ],
            6: [
                {
                    faces: [4, 3, 6, 1, 2, 5],
                    rotation: { x: Math.PI/2, y: Math.PI, z: 0 }
                },
                {
                    faces: [5, 2, 6, 1, 4, 3],
                    rotation: { x: -Math.PI/2, y: 0, z: Math.PI/2 }
                },
                {
                    faces: [3, 4, 6, 1, 5, 2],
                    rotation: { x: -Math.PI/2, y: 0, z: 0 }
                },
                {
                    faces: [2, 5, 6, 1, 3, 4],
                    rotation: { x: -Math.PI/2, y: 0, z: -Math.PI/2 }
                }
            ]
        };
        
        // 从可能的配置中随机选择一个
        const possibleConfigs = this.facesConfigurations[value] || this.facesConfigurations[1];
        // TODO 临时调试
        const randomIndex = Math.floor(Math.random() * possibleConfigs.length);
        // const randomIndex = 0;
        this.selectedConfig = possibleConfigs[randomIndex];
        const faces = this.selectedConfig.faces;
        
        console.log(`初始化骰子面值，value=${value}，faces布局:`, {
            右: faces[0],
            左: faces[1],
            上: faces[2],
            下: faces[3],
            前: faces[4],
            后: faces[5]
        });
        
        return faces;
    }

    // 获取当前前面的值
    get frontValue() {
        return this.faces[4];  // 数组第五个元素是前面的值
    }

    // 获取当前顶部的值
    get topValue() {
        return this.faces[2];  // 数组第三个元素是顶部的值
    }

    rotateFaces(direction) {
        const oldFaces = [...this.faces];
        console.log(`开始旋转骰子面值，方向: ${direction}`);
        console.log('旋转前的面值布局:', {
            右: oldFaces[0],
            左: oldFaces[1],
            上: oldFaces[2],
            下: oldFaces[3],
            前: oldFaces[4],
            后: oldFaces[5]
        });
        
        switch(direction) {
            case 'up':    // 绕X轴顺时针
                this.faces = [
                    oldFaces[0],  // 右面不变
                    oldFaces[1],  // 左面不变
                    oldFaces[4],  // 前面变上面
                    oldFaces[5],  // 后面变下面
                    oldFaces[3],  // 下面变前面
                    oldFaces[2]   // 上面变后面
                ];
                break;
                
            case 'down':  // 绕X轴逆时针
                this.faces = [
                    oldFaces[0],  // 右面不变
                    oldFaces[1],  // 左面不变
                    oldFaces[5],  // 后面变上面
                    oldFaces[4],  // 前面变下面
                    oldFaces[2],  // 上面变前面
                    oldFaces[3]   // 下面变后面
                ];
                break;
                
            case 'left':  // 绕Z轴顺时针
                this.faces = [
                    oldFaces[3],  // 下面变右面
                    oldFaces[2],  // 上面变左面
                    oldFaces[0],  // 右面变上面
                    oldFaces[1],  // 左面变下面
                    oldFaces[4],  // 前面不变
                    oldFaces[5]   // 后面不变
                ];
                break;
                
            case 'right': // 绕Z轴逆时针
                this.faces = [
                    oldFaces[2],  // 上面变右面
                    oldFaces[3],  // 下面变左面
                    oldFaces[1],  // 左面变上面
                    oldFaces[0],  // 右面变下面
                    oldFaces[4],  // 前面不变
                    oldFaces[5]   // 后面不变
                ];
                break;
        }

        console.log('旋转后的面值布局:', {
            右: this.faces[0],
            左: this.faces[1],
            上: this.faces[2],
            下: this.faces[3],
            前: this.faces[4],
            后: this.faces[5]
        });

        // 记录旋转前的网格旋转角度
        console.log('旋转前的网格旋转角度:', {
            x: (this.mesh.rotation.x * 180 / Math.PI).toFixed(2) + '°',
            y: (this.mesh.rotation.y * 180 / Math.PI).toFixed(2) + '°',
            z: (this.mesh.rotation.z * 180 / Math.PI).toFixed(2) + '°'
        });
    }

    // 验证骰子面值
    validateFaces() {
        // 确保对面之和为7
        if (this.faces[0] + this.faces[1] !== 7 ||  // front + back
            this.faces[2] + this.faces[3] !== 7 ||  // top + bottom
            this.faces[4] + this.faces[5] !== 7) {  // right + left
            console.error('Invalid dice faces after rotation:', this.faces);
        }
    }

    async rotate(direction) {
        if (this.isAnimating) {
            console.log('骰子正在旋转中，忽略新的旋转请求');
            return;
        }
        
        this.isAnimating = true;
        
        // 使用缓存的四元数和向量
        this._targetQuaternion.identity();
        this._worldAxis.set(0, 0, 0);
        const rotationAngle = Math.PI/2;
        
        // 根据方向设置世界坐标系的旋转轴和角度
        switch(direction) {
            case 'up':
                worldAxis.set(1, 0, 0); // 绕世界坐标系的X轴
                targetQuaternion.setFromAxisAngle(worldAxis, -rotationAngle);
                break;
            case 'down':
                worldAxis.set(1, 0, 0);
                targetQuaternion.setFromAxisAngle(worldAxis, rotationAngle);
                break;
            case 'left':
                worldAxis.set(0, 1, 0); // 绕世界坐标系的Y轴
                targetQuaternion.setFromAxisAngle(worldAxis, -rotationAngle);
                break;
            case 'right':
                worldAxis.set(0, 1, 0);
                targetQuaternion.setFromAxisAngle(worldAxis, rotationAngle);
                break;
        }

        // 获取当前的世界四元数
        const currentWorldQuaternion = this.mesh.getWorldQuaternion(new THREE.Quaternion());
        
        // 计算新的目标四元数（在世界坐标系中）
        const finalQuaternion = targetQuaternion.multiply(currentWorldQuaternion);

        // 执行旋转动画
        await new Promise((resolve) => {
            const duration = 1500; // 动画持续时间（毫秒）
            const startTime = Date.now();
            const startQuaternion = this.mesh.quaternion.clone();

            const animate = () => {
                const progress = (Date.now() - startTime) / duration;
                if (progress >= 1) {
                    this.isAnimating = false;
                    
                    // 更新faces数组
                    this.rotateFaces(direction);
                    
                    // 更新材质
                    this.updateMaterials();
                    
                    console.log('骰子旋转动画完成，更新后的面值:', {
                        右: this.faces[0],
                        左: this.faces[1],
                        上: this.faces[2],
                        下: this.faces[3],
                        前: this.faces[4],
                        后: this.faces[5]
                    });
                    
                    resolve();
                    return;
                }

                // 使用缓动函数使动画更平滑
                const easeProgress = 1 - Math.pow(1 - progress, 2);

                // 使用四元数插值实现平滑旋转
                THREE.Quaternion.slerp(startQuaternion, targetQuaternion, this.mesh.quaternion, easeProgress);

                requestAnimationFrame(animate);
            };

            animate();
        });
    }

    async createDiceMesh() {
        const self = this; // 保存this引用，以便在回调中使用
        return new Promise(async (resolve, reject) => {
            try {
                // 获取模型路径
                const modelPath = this.getModelPath();
                console.log('选择的模型:', modelPath);
                
                // 使用ModelLoader获取模型，如果缓存中有则直接使用，否则会加载
                const model = await modelLoader.getModel(modelPath);
                
                if (model) {
                    this.processLoadedModel(model);
                    
                    // 将骰子实例引用存储到mesh的userData中，方便射线检测时直接获取
                    model.userData.dice = self;
                    model.userData.isBomb = self.isBomb;
                    model.userData.isTeleport = self.isTeleport;
                    model.userData.value = self._value;
                    
                    // 遍历模型的所有子对象，确保每个子网格都能被射线检测到并获取骰子信息
                    model.traverse(child => {
                        if (child.isMesh) {
                            child.userData.dice = self;
                            child.userData.isBomb = self.isBomb;
                            child.userData.isTeleport = self.isTeleport;
                            child.userData.value = self._value;
                        }
                    });
                    
                    resolve(model);
                } else {
                    throw new Error('无法加载模型');
                }
            } catch (error) {
                console.error('创建骰子模型时出错:', error);
                reject(error);
            }
        });
    }

    updateMaterials() {
        // 由于使用GLB模型，不需要更新材质
        console.log('使用GLB模型，保持原有材质效果');
        
        // 仅用于调试目的，打印当前faces数组布局
        console.log('当前faces数组布局:', {
            右: this.faces[0],
            左: this.faces[1],
            上: this.faces[2],
            下: this.faces[3],
            前: this.faces[4],
            后: this.faces[5]
        });

        // 验证faces数组的正确性
        this.validateFaces();
    }

    setHighlight(highlighted) {
        console.log(`设置骰子高亮状态: ${highlighted ? '开启' : '关闭'}`);        
        this.isHighlighted = highlighted;
        if (this.mesh) {
            // 处理坐标轴辅助器
            if (highlighted) {
                // 保存原始位置和缩放值
                this.originalPosition.copy(this.mesh.position);
                this.originalScale.copy(this.mesh.scale);
                
                // 设置高亮状态的缩放
                const targetScale = this.mesh.scale.x * 1.5;
                console.log('高亮状态 - 原始缩放值:', {
                    x: this.originalScale.x.toFixed(2),
                    y: this.originalScale.y.toFixed(2),
                    z: this.originalScale.z.toFixed(2)
                });
                console.log('高亮状态 - 目标缩放值:', targetScale.toFixed(2));
                
                this.mesh.scale.set(targetScale, targetScale, targetScale);
                console.log('高亮状态 - 应用新的缩放值完成');
                // 设置选中骰子的高度
                this.mesh.position.y = gameConfig.dice.selectedHeight;
            } else {
                // 移除坐标轴辅助器
                if (this.axesHelper) {
                    this.mesh.remove(this.axesHelper);
                    this.axesHelper = null;
                    console.log('移除坐标轴辅助器');
                }
                
                // 直接恢复原始位置和缩放值
                this.mesh.position.copy(this.originalPosition);
                this.mesh.scale.copy(this.originalScale);
                console.log('取消高亮 - 恢复原始位置和缩放值:', {
                    position: {
                        x: this.originalPosition.x.toFixed(2),
                        y: this.originalPosition.y.toFixed(2),
                        z: this.originalPosition.z.toFixed(2)
                    },
                    scale: {
                        x: this.originalScale.x.toFixed(2),
                        y: this.originalScale.y.toFixed(2),
                        z: this.originalScale.z.toFixed(2)
                    }
                });
            }
        } else {
            console.warn('无法设置高亮状态：mesh对象不存在');
        }
    }

    setPreview(previewing) {
        this.isPreviewing = previewing;
        if (this.mesh) {
            // 预览效果 - 发光的绿色
            if (previewing) {
                this.mesh.material.forEach(mat => {
                    mat.emissive.setHex(0x00ff00);
                    mat.emissiveIntensity = 0.3;
                });
            } else {
                this.mesh.material.forEach(mat => {
                    mat.emissive.setHex(0x000000);
                    mat.emissiveIntensity = 0;
                });
            }
        }
    }

    getTopValue(frontValue) {
        // 标准右手性骰子布局中的顶面值
        const topValues = {
            1: 2,  // 前面是1时，上面是2（因为1朝上时2朝前）
            2: 1,  // 前面是2时，上面是1（因为2朝前时1朝上）
            3: 2,  // 前面是3时，上面是2
            4: 2,  // 前面是4时，上面是2
            5: 2,  // 前面是5时，上面是2
            6: 5   // 前面是6时，上面是5（因为6朝前时5朝下）
        };
        return topValues[frontValue];
    }

    getRightValue(frontValue) {
        // 标准右手性骰子布局中的右面值
        const rightValues = {
            1: 3,  // 前面是1时，右面是3（标准右手性：1朝上2朝前3朝右）
            2: 3,  // 前面是2时，右面是3
            3: 1,  // 前面是3时，右面是1（因为3朝前时1在右）
            4: 1,  // 前面是4时，右面是1
            5: 3,  // 前面是5时，右面是3
            6: 3   // 前面是6时，右面是3
        };
        return rightValues[frontValue];
    }

    // 处理触摸开始事件
    handleTouchStart(event) {
        if (!this.isSelected || this.isAnimating) return;
        
        this.isTouchActive = true;
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
    }

    // 处理触摸移动事件
    handleTouchMove(event) {
        if (!this.isTouchActive) return;
        
        this.touchEndX = event.touches[0].clientX;
        this.touchEndY = event.touches[0].clientY;
    }

    // 处理触摸结束事件
    handleTouchEnd() {
        if (!this.isTouchActive) return;
        
        this.isTouchActive = false;
        
        // 计算水平和垂直方向的位移
        const deltaX = this.touchEndX - this.touchStartX;
        const deltaY = this.touchEndY - this.touchStartY;
        
        // 如果位移太小，不触发移动
        if (Math.abs(deltaX) < this.minSwipeDistance && Math.abs(deltaY) < this.minSwipeDistance) {
            return;
        }
        
        // 判断主要的移动方向
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            // 水平移动
            if (deltaX > 0) {
                this.rotate('right');
            } else {
                this.rotate('left');
            }
        } else {
            // 垂直移动
            if (deltaY > 0) {
                this.rotate('down');
            } else {
                this.rotate('up');
            }
        }
    }
}
